/**
 * useWorkout Hook
 *
 * Manages active workout session state with Supabase persistence
 * Handles set logging, exercise selection, and workout completion
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';

export type WorkoutType = 'Push' | 'Pull' | 'Leg' | 'Arms' | 'Cardio' | 'Other';

type ExerciseCategory = 'chest' | 'back' | 'shoulders' | 'arms' | 'legs' | 'core' | 'cardio' | 'other';

interface WorkoutSet {
  id: string;
  exercise_id: string;
  exercise_name: string;
  exercise_category?: ExerciseCategory;
  set_number: number;
  reps: number | null;
  weight: number | null;
  intensity: number | null;
  notes: string | null;
  completed_at: string;
}

interface ActiveWorkout {
  id: string;
  started_at: string;
  workout_type: WorkoutType;
  timer_started_at: string | null;
  sets: WorkoutSet[];
}

interface UseWorkoutReturn {
  activeWorkout: ActiveWorkout | null;
  workoutType: WorkoutType | null;
  isSelectingType: boolean;
  startWorkout: () => void;
  selectWorkoutType: (type: WorkoutType) => Promise<void>;
  addSet: (exerciseName: string, reps?: number, weight?: number, intensity?: number, category?: ExerciseCategory) => Promise<void>;
  updateSet: (setId: string, reps?: number, weight?: number, intensity?: number) => Promise<void>;
  deleteSet: (setId: string) => Promise<void>;
  renameExercise: (oldName: string, newName: string) => Promise<void>;
  completeWorkout: () => Promise<void>;
  cancelWorkout: () => Promise<void>;
  elapsedTime: number;
  timerStarted: boolean;
  error: string | null;
}

/**
 * Maps workout type to exercise category for database storage
 */
function mapWorkoutTypeToCategory(workoutType: WorkoutType): ExerciseCategory {
  switch (workoutType) {
    case 'Push':
      return 'chest';
    case 'Pull':
      return 'back';
    case 'Leg':
      return 'legs';
    case 'Cardio':
      return 'cardio';
    case 'Other':
    default:
      return 'other';
  }
}

export function useWorkout(): UseWorkoutReturn {
  const { user } = useAuth();
  const [activeWorkout, setActiveWorkout] = useState<ActiveWorkout | null>(null);
  const [isSelectingType, setIsSelectingType] = useState(false);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Timer effect - starts only when timer_started_at is set (after first set added)
  useEffect(() => {
    if (activeWorkout?.timer_started_at) {
      const startTime = new Date(activeWorkout.timer_started_at).getTime();
      intervalRef.current = setInterval(() => {
        setElapsedTime(Math.floor((Date.now() - startTime) / 1000));
      }, 1000);
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [activeWorkout?.timer_started_at]);

  const startWorkout = useCallback(() => {
    setError(null);
    setIsSelectingType(true);
  }, []);

  const selectWorkoutType = useCallback(async (type: WorkoutType): Promise<void> => {
    if (!user) {
      setError('You must be logged in to start a workout');
      return;
    }

    setError(null);
    const now = new Date();

    // Ensure user exists in public.users table (may not exist if signed up before schema)
    const { data: existingUser } = await supabase
      .from('users')
      .select('id')
      .eq('id', user.id)
      .single();

    if (!existingUser) {
      // Create user record if it doesn't exist
      const { error: userError } = await supabase
        .from('users')
        .insert({
          id: user.id,
          email: user.email ?? '',
          display_name: user.user_metadata?.display_name ?? null,
        });

      if (userError) {
        setError(`Failed to create user profile: ${userError.message}`);
        return;
      }
    }

    // Create workout_log in Supabase
    const { data, error: insertError } = await supabase
      .from('workout_logs')
      .insert({
        user_id: user.id,
        started_at: now.toISOString(),
        status: 'in_progress',
        notes: type,
      })
      .select('id')
      .single();

    if (insertError) {
      setError(`Failed to start workout: ${insertError.message}`);
      return;
    }

    setActiveWorkout({
      id: data.id,
      started_at: now.toISOString(),
      workout_type: type,
      timer_started_at: null,
      sets: [],
    });
    setIsSelectingType(false);
  }, [user]);

  const addSet = useCallback(
    async (
      exerciseName: string,
      reps?: number,
      weight?: number,
      intensity?: number,
      category?: ExerciseCategory
    ): Promise<void> => {
      if (!activeWorkout || !user) return;

      setError(null);
      const now = new Date();
      const shouldStartTimer = activeWorkout.sets.length === 0 && !activeWorkout.timer_started_at;

      // Determine exercise category
      const exerciseCategory = category ?? mapWorkoutTypeToCategory(activeWorkout.workout_type);

      // Find or create the exercise
      let exerciseId: string | null = null;

      // Try to find existing exercise
      const { data: existing } = await supabase
        .from('exercises')
        .select('id')
        .eq('user_id', user.id)
        .eq('name', exerciseName)
        .single();

      if (existing) {
        exerciseId = existing.id;
      } else {
        // Create new exercise
        const { data: created, error: createError } = await supabase
          .from('exercises')
          .insert({
            user_id: user.id,
            name: exerciseName,
            category: exerciseCategory,
          })
          .select('id')
          .single();

        if (createError) {
          setError(`Failed to create exercise: ${createError.message}`);
          return;
        }

        exerciseId = created.id;
      }

      if (!exerciseId) return;

      // Calculate set number for this exercise
      const setNumber = activeWorkout.sets.filter((s) => s.exercise_name === exerciseName).length + 1;

      // Insert workout_log_set
      const { data: setData, error: insertSetError } = await supabase
        .from('workout_log_sets')
        .insert({
          workout_log_id: activeWorkout.id,
          exercise_id: exerciseId,
          set_number: setNumber,
          reps: reps ?? null,
          weight: weight ?? null,
          intensity: intensity ?? null,
          completed_at: now.toISOString(),
        })
        .select('id')
        .single();

      if (insertSetError) {
        setError(`Failed to add set: ${insertSetError.message}`);
        return;
      }

      const newSet: WorkoutSet = {
        id: setData.id,
        exercise_id: exerciseId,
        exercise_name: exerciseName,
        exercise_category: exerciseCategory,
        set_number: setNumber,
        reps: reps ?? null,
        weight: weight ?? null,
        intensity: intensity ?? null,
        notes: null,
        completed_at: now.toISOString(),
      };

      setActiveWorkout({
        ...activeWorkout,
        timer_started_at: shouldStartTimer ? now.toISOString() : activeWorkout.timer_started_at,
        sets: [...activeWorkout.sets, newSet],
      });
    },
    [activeWorkout, user]
  );

  const updateSet = useCallback(
    async (setId: string, reps?: number, weight?: number, intensity?: number): Promise<void> => {
      if (!activeWorkout) return;

      setError(null);

      // Update in database
      const { error: updateError } = await supabase
        .from('workout_log_sets')
        .update({
          reps: reps ?? null,
          weight: weight ?? null,
          intensity: intensity ?? null,
        })
        .eq('id', setId);

      if (updateError) {
        setError(`Failed to update set: ${updateError.message}`);
        return;
      }

      // Update local state
      setActiveWorkout({
        ...activeWorkout,
        sets: activeWorkout.sets.map((set) =>
          set.id === setId
            ? { ...set, reps: reps ?? null, weight: weight ?? null, intensity: intensity ?? null }
            : set
        ),
      });
    },
    [activeWorkout]
  );

  const deleteSet = useCallback(
    async (setId: string): Promise<void> => {
      if (!activeWorkout) return;

      setError(null);

      // Delete from database
      const { error: deleteError } = await supabase
        .from('workout_log_sets')
        .delete()
        .eq('id', setId);

      if (deleteError) {
        setError(`Failed to delete set: ${deleteError.message}`);
        return;
      }

      // Update local state - remove the set and recalculate set numbers
      const deletedSet = activeWorkout.sets.find((s) => s.id === setId);
      if (!deletedSet) return;

      const updatedSets = activeWorkout.sets
        .filter((s) => s.id !== setId)
        .map((set) => {
          // Recalculate set_number for sets of the same exercise that came after the deleted one
          if (set.exercise_name === deletedSet.exercise_name && set.set_number > deletedSet.set_number) {
            return { ...set, set_number: set.set_number - 1 };
          }
          return set;
        });

      setActiveWorkout({
        ...activeWorkout,
        sets: updatedSets,
      });
    },
    [activeWorkout]
  );

  const renameExercise = useCallback(
    async (oldName: string, newName: string): Promise<void> => {
      if (!activeWorkout || !user || oldName === newName) return;

      setError(null);

      // Find the exercise_id for this exercise
      const setsToUpdate = activeWorkout.sets.filter((s) => s.exercise_name === oldName);
      if (setsToUpdate.length === 0) return;

      const exerciseId = setsToUpdate[0]?.exercise_id;
      if (!exerciseId) return;

      // Update the exercise name in the database
      const { error: updateError } = await supabase
        .from('exercises')
        .update({ name: newName })
        .eq('id', exerciseId);

      if (updateError) {
        setError(`Failed to rename exercise: ${updateError.message}`);
        return;
      }

      // Update local state
      setActiveWorkout({
        ...activeWorkout,
        sets: activeWorkout.sets.map((set) =>
          set.exercise_name === oldName ? { ...set, exercise_name: newName } : set
        ),
      });
    },
    [activeWorkout, user]
  );

  const completeWorkout = useCallback(async (): Promise<void> => {
    if (!activeWorkout) return;

    setError(null);

    // Update workout_log status to completed
    const { error: updateError } = await supabase
      .from('workout_logs')
      .update({
        status: 'completed',
        completed_at: new Date().toISOString(),
      })
      .eq('id', activeWorkout.id);

    if (updateError) {
      setError(`Failed to complete workout: ${updateError.message}`);
      return;
    }

    setActiveWorkout(null);
    setElapsedTime(0);
    setIsSelectingType(false);
  }, [activeWorkout]);

  const cancelWorkout = useCallback(async (): Promise<void> => {
    if (activeWorkout) {
      // Update workout_log status to cancelled
      await supabase
        .from('workout_logs')
        .update({
          status: 'cancelled',
          completed_at: new Date().toISOString(),
        })
        .eq('id', activeWorkout.id);
    }

    setActiveWorkout(null);
    setElapsedTime(0);
    setIsSelectingType(false);
    setError(null);
  }, [activeWorkout]);

  return {
    activeWorkout,
    workoutType: activeWorkout?.workout_type ?? null,
    isSelectingType,
    startWorkout,
    selectWorkoutType,
    addSet,
    updateSet,
    deleteSet,
    renameExercise,
    completeWorkout,
    cancelWorkout,
    elapsedTime,
    timerStarted: activeWorkout?.timer_started_at !== null,
    error,
  };
}
